// src/components/Login.jsx
// UPDATED: Added Forgot Password flow (email reset link via Firebase)
// CONFIRMED: Email verification is active for all new users via:
// 1. Cloud Function sendEmailVerification (onCreate) → branded custom email
// 2. Client-side sendEmailVerification(user) → immediate dispatch after signup
// NOTE: New users MUST verify email before accessing the app (showVerifyOverlay gate)
// ALIGNED: Profile completion modal now unified for both email/password and Google sign-in
// SUPER ADMIN BYPASS: Hardcoded UIDs completely skip profile completion modal and go to admin portal

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, googleProvider, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc, setDoc, getDoc, serverTimestamp,
  updateDoc, addDoc, collection,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';

const GOLD = '#B8860B';
const NAVY = '#001F3F';
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];
const MAPS_API_KEY = 'AIzaSyB7tcsVKlfo3Ys5BoUrZV9_4XKWNJP_H10';
const RECAPTCHA_SITE_KEY = '6Lceg5YsAAAAAAlahPLVm03XenWp6ID42djjcPOT';

googleProvider.setCustomParameters({ prompt: 'select_account' });

const getCoordinates = async (address) => {
  if (!address) return { lat: 6.5244, lng: 3.3792 };
  try {
    const q = encodeURIComponent(`${address}, Lagos, Nigeria`);
    const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${q}&key=${MAPS_API_KEY}`);
    const data = await res.json();
    if (data.status === 'OK' && data.results?.[0]) return data.results[0].geometry.location;
    return { lat: 6.5244, lng: 3.3792 };
  } catch { return { lat: 6.5244, lng: 3.3792 }; }
};

export default function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Invite system ──────────────────────────────────────────────────────────
  const inviteEmail = searchParams.get('email') || '';
  const inviteName = searchParams.get('fname') || '';
  const inviteRole = searchParams.get('role') || 'user';
  const inviteBiz = searchParams.get('biz') || '';
  const hasInvite = !!searchParams.get('invite');
  const isRegisterMode = searchParams.get('mode') === 'register' || hasInvite;

  // ── Form state ─────────────────────────────────────────────────────────────
  const [isLogin, setIsLogin] = useState(!isRegisterMode);
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(inviteName);
  const [gender, setGender] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [isStylist, setIsStylist] = useState(inviteRole === 'stylist');
  const [stylistType, setStylistType] = useState('stylist');
  const [businessName, setBusinessName] = useState(inviteBiz);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failCount, setFailCount] = useState(0);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [showLegal, setShowLegal] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [showVerifyOverlay, setShowVerifyOverlay] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [tempUser, setTempUser] = useState(null); // For Google or incomplete email/password users
  const [resending, setResending] = useState(false);

  // ── Forgot password state ──────────────────────────────────────────────────
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // ── Load reCAPTCHA + Auth observer ────────────────────────────────────────
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user && showVerifyOverlay) {
        await user.reload();
        if (user.emailVerified) {
          toast.success('Identity Verified! Unlocking Biometric Scans...');
          setShowVerifyOverlay(false);
          const barberSnap = await getDoc(doc(db, 'barbers', user.uid));
          const role = barberSnap.exists() ? 'stylist' : 'user';
          navigate(role === 'stylist' ? '/stylist/dashboard' : '/dashboard');
        }
      }
    });

    return () => { unsub(); };
  }, [showVerifyOverlay, navigate]);

  // ── Routing helper ─────────────────────────────────────────────────────────
  const handleRouting = async (user, role) => {
    // SUPER ADMIN BYPASS — Skip everything and show admin portal
    if (SUPER_ADMINS.includes(user.uid)) {
      setShowPortal(true);
      return;
    }

    if (!user.emailVerified) {
      setShowVerifyOverlay(true);
      setLoading(false);
      return;
    }

    const targetCollection = role === 'stylist' ? 'barbers' : 'users';
    const docSnap = await getDoc(doc(db, targetCollection, user.uid));
    const data = docSnap.exists() ? docSnap.data() : {};

    const isProfileComplete = !!(
      data.fullName && 
      data.gender && 
      data.address && 
      data.phoneNumber
    );

    if (!isProfileComplete) {
      setTempUser({
        uid: user.uid,
        email: user.email,
        name: user.displayName || fullName || '',
        exists: docSnap.exists(),
        role: role
      });
      setFullName(data.fullName || user.displayName || fullName || '');
      setShowProfileCompletion(true);
      return;
    }

    navigate(role === 'stylist' ? '/stylist/dashboard' : '/dashboard');
  };

  // ── FORGOT PASSWORD ────────────────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { 
      toast.error('Please enter your email address.'); 
      return; 
    }
    setForgotLoading(true);
    try {
      await sendPasswordResetEmail(auth, forgotEmail.trim().toLowerCase(), {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: false,
      });
      setForgotSent(true);
      toast.success('Password reset link sent! Check your inbox.');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setForgotSent(true);
        toast.success('If that email is registered, a reset link has been sent.');
      } else {
        toast.error(err.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  // ── MAIN AUTH FLOW ─────────────────────────────────────────────────────────
  const handleAuth = async (e) => {
    e.preventDefault();
    if (!isLogin && !accepted) { 
      setShowLegal(true); 
      return; 
    }

    setLoading(true);
    try {
      // 1. reCAPTCHA
      const token = await new Promise((resolve, reject) => {
        if (!window.grecaptcha?.enterprise) {
          return reject(new Error('Security module not loaded. Please refresh.'));
        }
        window.grecaptcha.enterprise.ready(async () => {
          try { 
            resolve(await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action: isLogin ? 'LOGIN' : 'SIGNUP' })); 
          } catch (err) { reject(err); }
        });
      });

      const functions = getFunctions();
      const verifyAuthSecurity = httpsCallable(functions, 'verifyAuthSecurityV2');
      const verificationResult = await verifyAuthSecurity({ token, action: isLogin ? 'LOGIN' : 'SIGNUP' });
      if (!verificationResult.data?.success) throw new Error('Security check failed. Please try again.');

      let userCredential;
      const inviteId = searchParams.get('invite');
      let isFoundersFlow = false;

      if (isLogin) {
        // PATH 1: Normal Login
        userCredential = await signInWithEmailAndPassword(auth, email, password);
        const [barberSnap, userSnap] = await Promise.all([
          getDoc(doc(db, 'barbers', userCredential.user.uid)).catch(() => ({ exists: () => false })),
          getDoc(doc(db, 'users', userCredential.user.uid)).catch(() => ({ exists: () => false })),
        ]);
        let finalRole = 'user';
        if (barberSnap.exists()) finalRole = 'stylist';
        else if (userSnap.exists()) finalRole = userSnap.data()?.role || 'user';
        handleRouting(userCredential.user, finalRole);
      } else {
        // PATH 2 & 3: New Registration or Waitlist Upgrade
        if (!fullName || !gender || !address || !phone) {
          throw new Error('All profile fields (Full Name, Gender, Address, Phone) are required.');
        }

        const coords = await getCoordinates(address);

        try {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
        } catch (authErr) {
          if (authErr.code === 'auth/email-already-in-use') {
            isFoundersFlow = true;
            const upgradeFunction = httpsCallable(functions, 'upgradeWaitlistUser');
            const upgradeResult = await upgradeFunction({ 
              fullName, phone, gender, address, coords, 
              isStylist, 
              stylistType: isStylist ? stylistType : null, 
              businessName: isStylist ? businessName : null, 
              inviteId 
            });
            if (!upgradeResult.data.success) throw new Error('Upgrade failed. Please try again.');
            userCredential = await signInWithEmailAndPassword(auth, email, password);
          } else { 
            throw authErr; 
          }
        }

        const { user } = userCredential;
        await updateProfile(user, { displayName: fullName });

        if (!isFoundersFlow) {
          const payload = {
            uid: user.uid,
            email: email.toLowerCase().trim(),
            fullName,
            phoneNumber: phone,
            role: isStylist ? 'stylist' : 'user',
            stylistType: isStylist ? (stylistType || 'stylist') : 'client',
            businessName: isStylist ? (searchParams.get('biz') || businessName || 'Elite Stylist') : '',
            gender,
            address,
            coords,
            status: 'active',
            membershipLevel: (inviteId || isFoundersFlow) ? 'Founders Circle' : 'Standard',
            isVerified: false,
            onboardingComplete: true,
            hasAcceptedTerms: true,
            imageUsageConsent: true,
            earlyHealthAlerts: true,
            legalVersion: '2026.1.Copyright',
            lastActive: serverTimestamp(),
            inviteUsed: inviteId || null,
          };
          const targetCollection = isStylist ? 'barbers' : 'users';
          await setDoc(doc(db, targetCollection, user.uid), payload, { merge: true });
        }

        await addDoc(collection(db, 'admin_logs'), {
          action: isFoundersFlow ? 'FOUNDERS_UPGRADE' : 'NEW_REGISTRATION',
          detail: `${isStylist ? 'STYLIST' : 'USER'} ${isFoundersFlow ? 'upgraded' : 'registered'}: ${email}`,
          timestamp: serverTimestamp(),
          admin: 'SYSTEM_AUTH',
        });

        if (!user.emailVerified) {
          await sendEmailVerification(user);
          setShowVerifyOverlay(true);
          toast.success('Account created! Please check your email and verify before continuing.');
        } else {
          handleRouting(user, isStylist ? 'stylist' : 'user');
        }
      }
    } catch (err) {
      console.error('Auth Error:', err);
      if (isLogin) setFailCount(c => c + 1);
      toast.error(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification link resent!');
    } catch { 
      toast.error('Too many requests. Please wait a moment.'); 
    } finally { 
      setResending(false); 
    }
  };

  const checkVerificationStatus = async () => {
    await auth.currentUser.reload();
    if (auth.currentUser.emailVerified) {
      setShowVerifyOverlay(false);
      const barberSnap = await getDoc(doc(db, 'barbers', auth.currentUser.uid));
      handleRouting(auth.currentUser, barberSnap.exists() ? 'stylist' : 'user');
    } else {
      toast.error('Not verified yet. Please click the link in your email.');
    }
  };

  const handleGoogle = async () => {
    if (!accepted) { 
      toast.error('Please accept the Terms first.'); 
      return; 
    }
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // SUPER ADMIN BYPASS for Google sign-in
      if (SUPER_ADMINS.includes(result.user.uid)) {
        setShowPortal(true);
        setLoading(false);
        return;
      }

      const uSnap = await getDoc(doc(db, 'users', result.user.uid));
      const bSnap = await getDoc(doc(db, 'barbers', result.user.uid));
      const exists = uSnap.exists() || bSnap.exists();
      const userData = uSnap.exists() ? uSnap.data() : (bSnap.exists() ? bSnap.data() : null);

      if (!exists || (userData && (!userData.address || !userData.phoneNumber))) {
        setTempUser({
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || '',
          exists,
          role: bSnap.exists() ? 'stylist' : (uSnap.exists() ? userData?.role : (isStylist ? 'stylist' : 'user')),
        });
        setFullName(result.user.displayName || '');
        setShowProfileCompletion(true);
      } else {
        handleRouting(result.user, bSnap.exists() ? 'stylist' : (userData?.role || 'user'));
      }
    } catch { 
      toast.error('Google sign-in failed. Please try again.'); 
    } finally { 
      setLoading(false); 
    }
  };

  // ── UNIFIED PROFILE COMPLETION (Email/Password + Google) ─────────────────────
  // Super admins never reach here due to bypass in handleRouting and handleGoogle
  const handleProfileCompletion = async (e) => {
    e.preventDefault();
    if (!fullName || !gender || !address || !phone) { 
      toast.error('All fields are required.'); 
      return; 
    }

    setLoading(true);
    try {
      const isActuallyStylist = tempUser.role === 'stylist';
      const targetColl = isActuallyStylist ? 'barbers' : 'users';
      const coords = await getCoordinates(address);

      const basePayload = {
        uid: tempUser.uid,
        email: tempUser.email,
        fullName,
        phoneNumber: phone,
        gender,
        address,
        coords,
        onboardingComplete: true,
        lastActive: serverTimestamp()
      };

      if (tempUser.exists) {
        await updateDoc(doc(db, targetColl, tempUser.uid), basePayload);
      } else {
        await setDoc(doc(db, targetColl, tempUser.uid), {
          ...basePayload,
          role: isActuallyStylist ? 'stylist' : 'user',
          createdAt: serverTimestamp(),
          hasAcceptedTerms: true,
          legalVersion: '2026.1.Copyright'
        });
      }

      setShowProfileCompletion(false);
      toast.success('Profile completed successfully!');
      handleRouting(auth.currentUser || { uid: tempUser.uid, email: tempUser.email }, isActuallyStylist ? 'stylist' : 'user');
    } catch (err) {
      console.error(err);
      toast.error('Error saving profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: NAVY, display: 'grid', placeItems: 'center', padding: '20px', fontFamily: 'Montserrat, sans-serif' }}>
      {/* ── Email Verification Overlay ── */}
      {showVerifyOverlay && (
        <div style={modalOverlay}>
          <div style={portalBox}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🛡️</div>
            <h2 style={{ color: GOLD, margin: '0 0 10px' }}>VERIFY YOUR IDENTITY</h2>
            <p style={{ color: '#ccc', maxWidth: '300px', margin: '0 auto 20px', lineHeight: '1.6', fontSize: '0.88rem' }}>
              We've sent a verification link to <strong style={{ color: GOLD }}>{auth.currentUser?.email}</strong>.
              Click the link to unlock your AI Scanner.
            </p>
            <button style={portalBtn} onClick={checkVerificationStatus}>I'VE VERIFIED MY EMAIL</button>
            <button style={{ ...portalBtn, background: 'transparent', border: `1px solid ${GOLD}`, color: GOLD }} onClick={resendVerification} disabled={resending}>
              {resending ? 'SENDING...' : 'RESEND LINK'}
            </button>
            <p onClick={() => { signOut(auth); setShowVerifyOverlay(false); }} style={{ color: '#888', cursor: 'pointer', marginTop: '15px', fontSize: '0.8rem', textDecoration: 'underline' }}>
              Use a different email
            </p>
          </div>
        </div>
      )}

      {/* ── Forgot Password Modal ── */}
      {showForgotPassword && (
        <div style={modalOverlay}>
          <div style={{ ...portalBox, maxWidth: '380px' }}>
            {!forgotSent ? (
              <>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🔑</div>
                <h2 style={{ color: GOLD, margin: '0 0 8px', fontSize: '1.2rem', letterSpacing: '2px' }}>RESET PASSWORD</h2>
                <p style={{ color: '#888', fontSize: '0.82rem', margin: '0 0 20px', lineHeight: '1.6' }}>
                  Enter the email associated with your ESDRAS account. We'll send a secure reset link.
                </p>
                <form onSubmit={handleForgotPassword} style={{ width: '100%' }}>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                    style={{ ...inputStyle, marginBottom: '16px' }}
                  />
                  <button type="submit" disabled={forgotLoading} style={{ ...loginBtn, opacity: forgotLoading ? 0.7 : 1 }}>
                    {forgotLoading ? 'SENDING...' : 'SEND RESET LINK'}
                  </button>
                </form>
                <p onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }} style={{ color: '#888', cursor: 'pointer', marginTop: '16px', fontSize: '0.8rem', textDecoration: 'underline' }}>
                  Back to Login
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📬</div>
                <h2 style={{ color: GOLD, margin: '0 0 12px' }}>CHECK YOUR EMAIL</h2>
                <p style={{ color: '#ccc', fontSize: '0.85rem', lineHeight: '1.7', margin: '0 0 20px' }}>
                  If <strong style={{ color: GOLD }}>{forgotEmail}</strong> is registered, a reset link has been sent.
                  The link expires in 1 hour.
                </p>
                <p style={{ color: '#888', fontSize: '0.78rem', margin: '0 0 20px' }}>
                  Didn't receive it? Check your spam folder or try again.
                </p>
                <button onClick={() => { setForgotSent(false); setForgotEmail(''); }} style={{ ...portalBtn, background: 'transparent', border: `1px solid ${GOLD}`, color: GOLD }}>
                  TRY AGAIN
                </button>
                <button onClick={() => { setShowForgotPassword(false); setForgotEmail(''); setForgotSent(false); }} style={portalBtn}>
                  BACK TO LOGIN
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Unified Profile Completion Modal (Email/Password + Google) ── */}
      {/* Super admins never reach this modal due to bypass in handleRouting & handleGoogle */}
      {showProfileCompletion && (
        <div style={modalOverlay}>
          <div style={portalBox}>
            <h2 style={{ color: GOLD, margin: '0 0 16px' }}>COMPLETE YOUR PROFILE</h2>
            <form onSubmit={handleProfileCompletion}>
              <input 
                type="text" 
                placeholder="Full Name" 
                value={fullName} 
                onChange={e => setFullName(e.target.value)} 
                style={inputStyle} 
                required 
              />
              <input 
                type="tel" 
                placeholder="Phone Number" 
                value={phone} 
                onChange={e => setPhone(e.target.value)} 
                style={inputStyle} 
                required 
              />
              <input 
                type="text" 
                placeholder="Address" 
                value={address} 
                onChange={e => setAddress(e.target.value)} 
                style={inputStyle} 
                required 
              />
              <select 
                value={gender} 
                onChange={e => setGender(e.target.value)} 
                style={dropdownStyle} 
                required
              >
                <option value="" disabled>Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              <button type="submit" style={loginBtn} disabled={loading}>
                {loading ? 'SAVING...' : 'FINISH'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Admin Portal Picker ── */}
      {showPortal && (
        <div style={modalOverlay}>
          <div style={portalBox}>
            <h2 style={{ color: GOLD, marginBottom: '20px', letterSpacing: '2px' }}>ADMIN ACCESS</h2>
            <button style={portalBtn} onClick={() => { navigate('/admin'); setShowPortal(false); }}>GROOMING ADMIN</button>
            <button style={portalBtn} onClick={() => { navigate('/admin/merchant'); setShowPortal(false); }}>MERCHANT ADMIN</button>
            <button style={portalBtn} onClick={() => { navigate('/dashboard'); setShowPortal(false); }}>CLIENT VIEW</button>
            <button style={portalBtn} onClick={() => { navigate('/stylist/dashboard'); setShowPortal(false); }}>STYLIST VIEW</button>
            <p onClick={() => setShowPortal(false)} style={{ color: '#888', cursor: 'pointer', marginTop: '15px', fontSize: '0.8rem' }}>Cancel</p>
          </div>
        </div>
      )}

      {/* ── Legal Modal ── */}
      {showLegal && (
        <div style={modalOverlay}>
          <div style={legalBox}>
            <h2 style={{ color: GOLD, marginBottom: '15px' }}>{isStylist ? 'Stylist Agreement' : 'User Terms & Privacy'}</h2>
            <div style={scrollArea}>
              <div style={legalWarningCard}>
                <h3 style={{ color: GOLD, marginTop: 0, fontSize: '0.9rem' }}>COPYRIGHT & IMAGE USAGE NOTICE</h3>
                <p style={{ color: 'white', fontWeight: 'bold' }}>Users by signing to ESDRAS have given the platform permission to use their images for exclusive use by the platform.</p>
              </div>
              <h4 style={{ color: GOLD }}>1. TERMS OF AGREEMENT</h4>
              <p><strong>Acceptance:</strong> By creating an account, you agree to these terms. ESDRAS provides AI-powered hairstyle visualization and a booking marketplace connecting users with premium stylists in Lagos.</p>
              <p><strong>Conduct:</strong> Users agree not to upload offensive imagery. ESDRAS reserves the right to terminate accounts that attempt to reverse-engineer 3D projection mapping technology.</p>
              <p><strong>Limitation of Liability:</strong> ESDRAS is a visualization tool. We do not guarantee that a physical haircut will perfectly match a 3D digital preview.</p>
              <h4 style={{ color: GOLD, marginTop: '20px' }}>2. PRIVACY POLICY</h4>
              <p><strong>Data Collection:</strong> We collect personal identification and Biometric Geometry Data (3D head scans) required for the "Try-On" engine. Users who opt-in receive Early Access to Health Alerts based on cranial analysis.</p>
              <p><strong>Use:</strong> Scans are used exclusively for personalized avatars. Location is used to suggest stylists in proximity within Lagos.</p>
              <p><strong>Security:</strong> Data is stored via Firebase (Google Cloud) with industry-standard encryption.</p>
              <h4 style={{ color: GOLD, marginTop: '20px' }}>3. IMAGE & BIOMETRIC RIGHTS</h4>
              <p><strong>Ownership:</strong> You retain ownership of original photos but grant ESDRAS a royalty-free license to convert images into 3D assets for platform use.</p>
              <p><strong>Consent:</strong> You explicitly consent to facial feature analysis to create 3D head models for accurate "Architecture Volume" projection.</p>
            </div>
            <button onClick={() => { setAccepted(true); setShowLegal(false); }} style={closeBtn}>I AGREE & CONTINUE</button>
          </div>
        </div>
      )}

      {/* ── Main Login / Register Card ── */}
      <div style={formCard}>
        <img src="/icons/esdras-logo.png" style={{ width: '80px', marginBottom: '10px' }} alt="Esdras Logo" />
        <h2 style={{ color: GOLD, margin: '0 0 20px' }}>{isLogin ? 'LOGIN' : 'JOIN ESDRAS'}</h2>
        <form onSubmit={handleAuth} style={{ width: '100%' }}>
          {/* Role Toggle */}
          <div style={roleToggle}>
            <button type="button" onClick={() => setIsStylist(false)} style={!isStylist ? toggleActive : toggleInactive}>CLIENT</button>
            <button type="button" onClick={() => setIsStylist(true)} style={isStylist ? toggleActive : toggleInactive}>STYLIST</button>
          </div>

          {!isLogin && (
            <input 
              type="text" 
              placeholder="Full Name" 
              value={fullName} 
              onChange={e => setFullName(e.target.value)} 
              style={inputStyle} 
              required 
            />
          )}

          <input 
            type="email" 
            placeholder="Email" 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            style={inputStyle} 
            required 
          />
          <input 
            type="password" 
            placeholder="Password" 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
            style={inputStyle} 
            required 
          />

          {/* Forgot password link — only shown in login mode */}
          {isLogin && (
            <p style={{ textAlign: 'right', margin: '-4px 0 12px', fontSize: '0.75rem' }}>
              <span
                onClick={() => { setForgotEmail(email); setShowForgotPassword(true); setForgotSent(false); }}
                style={{ color: GOLD, cursor: 'pointer', textDecoration: 'underline' }}>
                Forgot Password?
              </span>
            </p>
          )}

          {!isLogin && (
            <>
              <input 
                type="tel" 
                placeholder="Phone Number" 
                value={phone} 
                onChange={e => setPhone(e.target.value)} 
                style={inputStyle} 
                required 
              />
              {isStylist && (
                <select 
                  value={stylistType} 
                  onChange={e => setStylistType(e.target.value)} 
                  style={dropdownStyle} 
                  required
                >
                  <option value="stylist">Stylist (Hair/Salon)</option>
                  <option value="barber">Barber</option>
                </select>
              )}
              <select 
                value={gender} 
                onChange={e => setGender(e.target.value)} 
                style={dropdownStyle} 
                required
              >
                <option value="" disabled>Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              <input 
                type="text" 
                placeholder={isStylist ? 'Business Address' : 'Residential Address'} 
                value={address} 
                onChange={e => setAddress(e.target.value)} 
                style={inputStyle} 
                required 
              />
            </>
          )}

          <div style={checkRow}>
            <input 
              type="checkbox" 
              checked={accepted} 
              onChange={e => setAccepted(e.target.checked)} 
            />
            <label style={{ fontSize: '0.75rem', color: '#ccc' }}>
              Agree to{' '}
              <span 
                onClick={() => setShowLegal(true)} 
                style={{ color: GOLD, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Terms & Privacy
              </span>
            </label>
          </div>

          <button 
            type="submit" 
            disabled={loading} 
            style={{ ...loginBtn, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'VERIFYING...' : isLogin ? 'LOGIN' : 'CREATE ACCOUNT'}
          </button>

          <button type="button" onClick={handleGoogle} style={googleBtn}>
            <img 
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
              alt="G" 
              style={{ width: '18px', marginRight: '10px' }} 
            />
            Continue with Google
          </button>

          <p style={{ marginTop: '20px', fontSize: '0.8rem', color: '#888' }}>
            {isLogin ? 'New here?' : 'Already joined?'}
            <span 
              onClick={() => setIsLogin(!isLogin)} 
              style={{ color: GOLD, cursor: 'pointer', marginLeft: '5px', fontWeight: '700' }}
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </span>
          </p>
        </form>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const GOLD_CONST = '#B8860B';
const modalOverlay = { 
  position: 'fixed', 
  inset: 0, 
  background: 'rgba(0,0,0,0.95)', 
  zIndex: 10000, 
  display: 'grid', 
  placeItems: 'center', 
  padding: '20px' 
};
const portalBox = { 
  background: '#0a0f14', 
  padding: '40px', 
  borderRadius: '30px', 
  border: `2px solid ${GOLD_CONST}`, 
  textAlign: 'center', 
  width: '100%', 
  maxWidth: '360px' 
};
const portalBtn = { 
  display: 'block', 
  width: '240px', 
  padding: '15px', 
  margin: '12px auto', 
  background: GOLD_CONST, 
  color: 'black', 
  border: 'none', 
  borderRadius: '12px', 
  fontWeight: 'bold', 
  cursor: 'pointer', 
  fontFamily: 'Montserrat, sans-serif', 
  fontSize: '0.85rem', 
  letterSpacing: '1px' 
};
const formCard = { 
  background: 'rgba(255,255,255,0.03)', 
  padding: '40px', 
  borderRadius: '30px', 
  width: '90%', 
  maxWidth: '400px', 
  textAlign: 'center', 
  border: '1px solid rgba(184,134,11,0.2)' 
};
const roleToggle = { 
  display: 'flex', 
  width: '100%', 
  background: 'rgba(0,0,0,0.3)', 
  borderRadius: '50px', 
  padding: '5px', 
  marginBottom: '20px' 
};
const toggleActive = { 
  flex: 1, 
  padding: '10px', 
  background: GOLD_CONST, 
  color: 'black', 
  border: 'none', 
  borderRadius: '50px', 
  fontWeight: 'bold', 
  fontFamily: 'Montserrat, sans-serif', 
  cursor: 'pointer' 
};
const toggleInactive = { 
  flex: 1, 
  padding: '10px', 
  background: 'transparent', 
  color: GOLD_CONST, 
  border: 'none', 
  cursor: 'pointer', 
  fontFamily: 'Montserrat, sans-serif' 
};
const inputStyle = { 
  width: '100%', 
  padding: '15px', 
  marginBottom: '10px', 
  borderRadius: '12px', 
  border: '1px solid #333', 
  background: '#000', 
  color: '#fff', 
  fontSize: '1rem', 
  fontFamily: 'Montserrat, sans-serif', 
  boxSizing: 'border-box' 
};
const dropdownStyle = { 
  ...inputStyle, 
  appearance: 'none', 
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23B8860B' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, 
  backgroundRepeat: 'no-repeat', 
  backgroundPosition: 'right 15px center', 
  backgroundSize: '15px' 
};
const loginBtn = { 
  width: '100%', 
  padding: '18px', 
  background: GOLD_CONST, 
  color: 'black', 
  border: 'none', 
  borderRadius: '50px', 
  fontWeight: 'bold', 
  cursor: 'pointer', 
  fontFamily: 'Montserrat, sans-serif', 
  fontSize: '0.95rem', 
  letterSpacing: '1px' 
};
const googleBtn = { 
  width: '100%', 
  padding: '15px', 
  marginTop: '10px', 
  background: '#fff', 
  color: '#000', 
  border: 'none', 
  borderRadius: '50px', 
  cursor: 'pointer', 
  fontWeight: 'bold', 
  display: 'flex', 
  alignItems: 'center', 
  justifyContent: 'center', 
  fontFamily: 'Montserrat, sans-serif' 
};
const legalBox = { 
  background: '#050505', 
  padding: '30px', 
  borderRadius: '25px', 
  maxWidth: '500px', 
  width: '90%', 
  border: `1px solid ${GOLD_CONST}` 
};
const scrollArea = { 
  height: '350px', 
  overflowY: 'auto', 
  color: '#aaa', 
  fontSize: '0.85rem', 
  textAlign: 'left' 
};
const closeBtn = { 
  width: '100%', 
  marginTop: '20px', 
  padding: '15px', 
  background: GOLD_CONST, 
  color: 'black', 
  border: 'none', 
  borderRadius: '50px', 
  fontWeight: 'bold', 
  cursor: 'pointer', 
  fontFamily: 'Montserrat, sans-serif' 
};
const legalWarningCard = { 
  border: `1px solid ${GOLD_CONST}`, 
  padding: '15px', 
  borderRadius: '10px', 
  marginBottom: '20px', 
  background: 'rgba(184,134,11,0.1)' 
};
const checkRow = { 
  display: 'flex', 
  gap: '10px', 
  marginBottom: '20px', 
  alignItems: 'center', 
  justifyContent: 'center' 
};
